/**
 * Centralized authentication session management.
 *
 * Replaces scattered auth patterns with a single abstraction:
 * - Token persistence (localStorage)
 * - Authenticated GraphQL client creation
 * - Viewer fetching and Redux state sync
 * - Rate display cookie management
 */
import { setUserContext } from '@/actions/ui';
import config from '@/config';
import { GraphQLClient, bearerAuth } from '@/core/api';
import { getPermissionService } from '@/core/permissions';
import { getDefaultUserContext } from '@/core/user';
import { Viewer, fetchViewer } from '@/core/viewer';
import store, { getThunkExtraArg } from '@/store';
import { setRuntimeVariable } from '@/store/runtime';
import { clearCache, getCache, rateDisplay, setCache } from '@/utils';

import { setViewer } from '../store/viewerSlice';
import { AUTH_TOKEN_CACHE_KEY as TOKEN_KEY } from './authTokenKey';
import {
  SessionOrigin,
  assertSessionOrigin,
  captureSessionOrigin,
  discardCognitoSession,
  isCognitoSessionActive,
  peekCognitoIdToken,
  peekCognitoUserId,
} from './cognitoSession';

/** The websocket calls the Cognito token listener makes (ENG-6136). */
interface TokenSocket {
  reset(): void;
  refreshToken(): void;
}

interface TokenCache {
  token: string;
}

export const authSession = {
  /**
   * Store auth token in localStorage
   */
  setToken(token: string): void {
    setCache(TOKEN_KEY, { token } as TokenCache);
  },

  /**
   * The token the api is sent: the Cognito ID token while a Cognito session is
   * active (ENG-6103), else the legacy token from localStorage.
   */
  getToken(): string | null {
    if (isCognitoSessionActive()) return peekCognitoIdToken() ?? null;
    const cached = getCache(TOKEN_KEY) as TokenCache | undefined;
    return cached?.token ?? null;
  },

  /**
   * Clear auth token from localStorage and runtime Redux state.
   *
   * ENG-5251 (PR #2688 review): also dispatches `setRuntimeVariable('token',
   * undefined)` so the ApiWebSocket token-getter (boot-time lazy accessor at
   * `index.tsx:252`) sees the logout symmetrically with mid-session signup.
   * Previously logout left `runtime.token` stale until the next page reload.
   */
  clearToken(): void {
    clearCache(TOKEN_KEY);
    authSession.clearLocalIdentity();
  },

  /**
   * ENG-6136: this tab's copy of the identity, without the shared legacy
   * token, which by then may belong to an identity that replaced it.
   */
  clearLocalIdentity(): void {
    store.dispatch(setRuntimeVariable('token', undefined));
    // ENG-5251 (PR #2688 review SEC-1): tear down the WebSocket
    // subscription registry on identity teardown so the next authenticated
    // socket doesn't replay the prior identity's subscribe frames. Covers
    // both logout and loginAs (loginAs → logout() → clearToken()). Guarded
    // because thunkExtraArg.apiWebSocket is unset until app boot.
    getThunkExtraArg().apiWebSocket?.reset();
  },

  /**
   * The websocket's answer to a new Cognito ID token. Usually a refresh of
   * the viewer's own token: reconnect with it, replaying the subscriptions.
   * ENG-6136: a token for another user than the viewer is an identity switch
   * (the new-customer modal signing in as B after A), so the subscriptions are
   * reset first, or A's resources would be replayed on B's socket. A token or
   * viewer without a user id says nothing either way, and resetting on every
   * hourly refresh would drop subscriptions nobody renews. Undefined
   * means the session is ending and a teardown follows: nothing to do.
   */
  followCognitoToken(socket: TokenSocket, token: string | undefined): void {
    if (!token) return;
    const userId = peekCognitoUserId();
    const viewerId = store.getState().viewer.id;
    if (userId && viewerId && userId !== viewerId) socket.reset();
    socket.refreshToken();
  },

  /**
   * Create an authenticated GraphQL client.
   * Uses provided token or falls back to cached token.
   */
  createClient(token?: string): GraphQLClient {
    // A Cognito ID token expires within the hour: with no explicit token, let
    // authLink fetch (and refresh) it per request instead of capturing it.
    if (!token && isCognitoSessionActive()) return new GraphQLClient(config.apiUrl);
    const authToken = token ?? this.getToken();
    if (!authToken) {
      throw new Error('No auth token available');
    }
    return new GraphQLClient(config.apiUrl, bearerAuth(authToken));
  },

  /**
   * Full authentication flow:
   * 1. Store token in cache
   * 2. Create authenticated client
   * 3. Fetch viewer data
   * 4. Sync Redux state
   * 5. Set rate display cookie
   *
   * @returns The authenticated viewer
   *
   * ENG-6102: a legacy (HS256) login while a Cognito session is active is not
   * supported. getApiToken() keeps sending the Cognito ID token, so the
   * identity on the wire would differ from the viewer stored here. Moving
   * login onto Cognito is ENG-6103's.
   *
   * ENG-6106: `skipMfaGateRedirect` is for a caller that answers the api's MFA
   * gate itself; the viewer fetch then leaves the 403 to it.
   *
   * ENG-6136: `origin` is the session the caller's operation started under.
   * A Cognito session that ended or was replaced since, before the call or
   * during the viewer fetch, throws CognitoSessionEndedError before any token,
   * viewer or context is stored. Legacy authentication is unchanged, and so
   * is another identity taking over on purpose (an OTP token).
   */
  async authenticate(
    token: string,
    { skipMfaGateRedirect, origin }: { skipMfaGateRedirect?: boolean; origin?: SessionOrigin } = {}
  ): Promise<Viewer> {
    if (origin) assertSessionOrigin(origin);
    // ENG-6103: a token that is not the Cognito ID token means another
    // identity is taking over (say, the OTP flow after a Cognito sign-in whose
    // viewer fetch failed). Drop the Cognito session first, or getApiToken
    // keeps sending its token while Redux holds this viewer.
    if (isCognitoSessionActive() && token !== peekCognitoIdToken()) {
      await discardCognitoSession();
    }
    const session = captureSessionOrigin();
    const client = new GraphQLClient(config.apiUrl, bearerAuth(token), { skipMfaGateRedirect });
    const viewer = await fetchViewer(client.client); // Validate first
    assertSessionOrigin(session);

    // ENG-6103: while a Cognito session is active it owns the api token: it
    // refreshes the ID token and feeds the websocket itself. A legacy token
    // cached now (the ID token, or one an HS256 flow minted) would be ignored
    // by getApiToken and outlive the session.
    if (!isCognitoSessionActive()) {
      this.setToken(token); // Only store if successful
      // ENG-5251 (PR #2688 review): dispatch runtime.token so the
      // ApiWebSocket lazy token-getter (index.tsx:252) sees the new auth.
      // Without this, mid-session signup would never propagate to the
      // WebSocket — currentToken === lastToken (both stale boot value), the
      // token-swap branch is skipped, and the socket stays anonymous.
      store.dispatch(setRuntimeVariable('token', token));
    }
    store.dispatch(setViewer(viewer));

    // Rebind the boot-time PermissionService instance to the new viewer
    // ID. The router middleware context captured the boot-time service
    // (constructed with viewer.id="" for a logged-out self-service signup),
    // so every route loader's permission.allowedBatch call would otherwise
    // query the API with an empty userId — yielding null results and
    // throwing inside buildTeamNav (TypeError on perms[0].allowed →
    // "Something went wrong" on first Settings visit). Rebinding the same
    // instance updates every captured reference at once.
    getPermissionService()?.rebindUserId(viewer.id);

    // Re-derive userContextOptions from the freshly-fetched viewer so any
    // newly-acquired team (e.g., post-signup, post-team-join) shows up in
    // the Settings nav and elsewhere without forcing a page reload. The
    // app-boot path in index.tsx runs this once at start; for mid-session
    // authentication we run it here. Preserves the user's chosen
    // userContext via getDefaultUserContext's currentContext arg.
    const { userContext } = store.getState().ui;
    await store.dispatch(setUserContext(getDefaultUserContext(viewer, userContext)));

    // Set rate display cookie (persists after logout)
    rateDisplay.setFromBillingAccount(viewer.groups?.[0]?.billing_account);

    return viewer;
  },

  /**
   * Check if user is authenticated: a legacy token in cache or an active
   * Cognito session.
   */
  isAuthenticated(): boolean {
    if (isCognitoSessionActive()) return true;
    return this.getToken() !== null;
  },
};
